import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { Pressable, Text, View } from 'react-native'
import styled from 'styled-components/native'
import {
  CompositeDecorator,
  ContentBlock,
  ContentState,
  convertFromRaw,
  convertToRaw,
  DefaultDraftBlockRenderMap,
  DraftHandleValue,
  Editor,
  EditorState,
  RawDraftContentState,
  RichUtils,
  SelectionState
} from 'draft-js'
import { Map } from 'immutable'
import 'draft-js/dist/Draft.css'
import { BlockComponentProps } from '../../../../doclets/types'
import { safeLink } from '../../../../doclets/links'
import {
  appendPresetRow,
  isRowPreset,
  RowPreset
} from '../../../../doclets/blocks/text/presets'
import { builderColors } from '../builderColors'
import { colors } from '../../../../doclets/tokens'
import { useDocletLayout } from '../../../../doclets/DocletLayoutContext'
import LinkModal from '../../../../components/RichTextEditor/LinkModal'
import useTranslation from '../../../../hooks/useTranslation'
import {
  blockClass,
  injectTextEditorStyles,
  TEXT_EDITOR_CLASS,
  WIDE_CLASS
} from './textEditorStyles'
import { linkRangeAtSelection, splitAfterHeading } from './textEditing'
import TextFormatBar from './TextFormatBar'

// Doclet links: legacy's plain browser blue, not MI6's underlined brand colour
const findLinkEntities = (
  block: ContentBlock,
  callback: (start: number, end: number) => void,
  contentState: ContentState
) =>
  block.findEntityRanges(character => {
    const key = character.getEntity()
    return key !== null && contentState.getEntity(key).getType() === 'LINK'
  }, callback)

const DocletLink = ({ children }: { children: ReactNode }) => (
  <span style={{ color: colors.link }}>{children}</span>
)

const decorator = new CompositeDecorator([
  { strategy: findLinkEntities, component: DocletLink }
])

// Same custom block types as MI6 Documents' editor, so content is portable,
// plus doclet-only small print (Documents show it as body text)
const blockRenderMap = DefaultDraftBlockRenderMap.merge(
  Map({
    heading: { element: 'h3' },
    'sub-heading': { element: 'h4' },
    small: { element: 'div' }
  })
)

interface DocletTextEditorProps {
  body: RawDraftContentState
  onBodyChange?: (body: RawDraftContentState) => void
  onFocus?: () => void
  selected?: boolean
  /**
   * Arranges the formatting row (null unless selected) and the text. By
   * default the row sits directly above the text; Image + text puts it above
   * the image too, so the two stay level when the row appears.
   */
  layout?: (parts: { formatBar: ReactNode; text: ReactNode }) => ReactNode
  /**
   * For the Date list and Link list presets: offers "+ Add row" under the
   * text, which appends another pre-formatted row (presets.ts)
   */
  rowPreset?: RowPreset | null
}

const stackedLayout = ({
  formatBar,
  text
}: {
  formatBar: ReactNode
  text: ReactNode
}) => (
  <>
    {formatBar}
    {text}
  </>
)

/**
 * Doclet rich text, edited in place: Draft.js styled like the finished page
 * (textEditorStyles). While selected, a compact formatting row
 * (TextFormatBar) sits at its top, pushing the text down rather than
 * covering the block above. Used by the Text and Image + text blocks.
 */
export const DocletTextEditor = ({
  body,
  onBodyChange,
  onFocus,
  selected,
  layout = stackedLayout,
  rowPreset = null
}: DocletTextEditorProps) => {
  const { t } = useTranslation()
  const { isWide } = useDocletLayout()
  const editor = useRef<Editor>(null)
  const [editorState, setEditorState] = useState(() =>
    EditorState.createWithContent(convertFromRaw(body), decorator)
  )
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [linkRefused, setLinkRefused] = useState(false)

  useEffect(injectTextEditorStyles, [])

  const update = (next: EditorState) => {
    const contentChanged =
      next.getCurrentContent() !== editorState.getCurrentContent()
    setEditorState(next)
    // Selection-only changes aren't edits, so they don't trigger autosave
    if (contentChanged) {
      onBodyChange?.(convertToRaw(next.getCurrentContent()))
    }
  }

  const handleKeyCommand = (
    command: string,
    state: EditorState
  ): DraftHandleValue => {
    const next = RichUtils.handleKeyCommand(state, command)
    if (next) {
      update(next)
      return 'handled'
    }
    return 'not-handled'
  }

  const handleReturn = (): DraftHandleValue => {
    const next = splitAfterHeading(editorState)
    if (!next) return 'not-handled'
    update(next)
    return 'handled'
  }

  const selection = editorState.getSelection()
  const inlineStyles = editorState.getCurrentInlineStyle()
  const blockType = RichUtils.getCurrentBlockType(editorState)
  const hasRange = !selection.isCollapsed()
  const linkRange = linkRangeAtSelection(editorState)

  const submitLink = () => {
    if (!linkUrl.trim()) {
      setLinkOpen(false)
      return
    }
    // "world50.com" -> "https://world50.com"; javascript: etc. refused, with
    // the dialog left open to correct it
    const url = safeLink(linkUrl)
    if (!url) {
      setLinkRefused(true)
      return
    }
    setLinkOpen(false)
    const content = editorState
      .getCurrentContent()
      .createEntity('LINK', 'MUTABLE', { url })
    const withEntity = EditorState.set(editorState, { currentContent: content })
    update(
      RichUtils.toggleLink(
        withEntity,
        withEntity.getSelection(),
        content.getLastCreatedEntityKey()
      )
    )
  }

  const formatBar = selected ? (
    <TextFormatBar
      format={{
        blockType,
        bold: inlineStyles.has('BOLD'),
        italic: inlineStyles.has('ITALIC'),
        inLink: !!linkRange,
        hasRange
      }}
      onBlockType={type => update(RichUtils.toggleBlockType(editorState, type))}
      onInlineStyle={style =>
        update(RichUtils.toggleInlineStyle(editorState, style))
      }
      onLink={() => {
        if (linkRange) {
          update(RichUtils.toggleLink(editorState, linkRange, null))
        } else {
          setLinkUrl('')
          setLinkRefused(false)
          setLinkOpen(true)
        }
      }}
    />
  ) : null

  // List presets: add another pre-formatted row and put the cursor at it
  const addRow = () => {
    if (!rowPreset) return
    const raw = appendPresetRow(
      convertToRaw(editorState.getCurrentContent()),
      rowPreset
    )
    const content = convertFromRaw(raw)
    const pushed = EditorState.push(editorState, content, 'insert-fragment')
    const rowKey = raw.blocks[raw.blocks.length - 1].key
    update(
      EditorState.forceSelection(pushed, SelectionState.createEmpty(rowKey))
    )
    onFocus?.()
    // Typing goes straight into the new row
    setTimeout(() => editor.current?.focus(), 0)
  }

  const text = (
    <>
      <div className={`${TEXT_EDITOR_CLASS}${isWide ? ` ${WIDE_CLASS}` : ''}`}>
        <Editor
          ref={editor}
          editorState={editorState}
          onChange={update}
          onFocus={onFocus}
          handleKeyCommand={handleKeyCommand}
          handleReturn={handleReturn}
          blockStyleFn={contentBlock => blockClass(contentBlock.getType())}
          blockRenderMap={blockRenderMap}
          placeholder={t('doclets:text:placeholder')}
        />
      </div>
      {rowPreset && (
        <Pressable
          accessibilityRole="button"
          onPress={addRow}
          style={{ alignSelf: 'flex-start', marginTop: 4, marginBottom: 12 }}
        >
          <AddRowText>
            {t(
              rowPreset === 'dateList'
                ? 'doclets:text:addDateRow'
                : 'doclets:text:addLinkRow'
            )}
          </AddRowText>
        </Pressable>
      )}
    </>
  )

  return (
    <View>
      {layout({ formatBar, text })}
      <LinkModal
        isOpen={linkOpen}
        onClose={() => setLinkOpen(false)}
        url={linkUrl}
        onUrlChange={url => {
          setLinkUrl(url)
          setLinkRefused(false)
        }}
        onSubmit={submitLink}
        error={linkRefused ? t('doclets:text:linkRefused') : null}
        focusOnOpen
      />
    </View>
  )
}

// Editable Text block
const TextBlockEditor = ({
  block,
  onChange,
  onFocus,
  selected
}: BlockComponentProps<'text'>) => (
  <DocletTextEditor
    body={block.props.body}
    // Keeps the block's other props (e.g. its preset): only the body is
    // edited here
    onBodyChange={body => onChange?.({ ...block.props, body })}
    onFocus={onFocus}
    selected={selected}
    rowPreset={isRowPreset(block.props.preset) ? block.props.preset : null}
  />
)

const AddRowText = styled(Text)`
  font-size: 13px;
  font-weight: 600;
  color: ${builderColors.tag};
`

export default TextBlockEditor
